// Depth for cooldown bar: same as health bars
const COOLDOWN_BAR_DEPTH = 1001;
import Phaser from 'phaser';

/**
 * AimCooldownBar UI component for Phaser games.
 * Shows aim delay progress as a yellow bar that fills from left to right.
 * Flashes once when full and then disappears.
 */
export class AimCooldownBar {
    private scene: Phaser.Scene;
    private x: number;
    private y: number;
    private width: number;
    private height: number;
    private progress: number; // 0 to 1
    private graphics: Phaser.GameObjects.Graphics;
    public bar: Phaser.GameObjects.Container; // Public container for adding to player
    private wasFull: boolean = false;
    private isFlashing: boolean = false;

    constructor(scene: Phaser.Scene, x: number, y: number, width: number, height: number) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.progress = 1; // Start at full (ready to attack)
        this.graphics = scene.add.graphics();

        // Create container at the center of where the bar should be
        // This allows scaling from the center
        this.bar = scene.add.container(x + width / 2, y + height / 2, [this.graphics]);
        this.bar.setDepth(COOLDOWN_BAR_DEPTH);
        this.bar.setVisible(false); // Start hidden since we're at full
        this.wasFull = true;
    }

    /**
     * Sets the aim cooldown progress (0 = not ready, 1 = ready to attack).
     */
    setProgress(progress: number) {
        const oldProgress = this.progress;
        this.progress = Phaser.Math.Clamp(progress, 0, 1);

        // Show bar when progress drops below 1 (aiming just started)
        if (oldProgress >= 1 && this.progress < 1) {
            this.wasFull = false;
            this.bar.setVisible(true);
            this.drawBar();
        }
        // When reaching full, flash and hide
        else if (!this.wasFull && this.progress >= 1 && !this.isFlashing) {
            this.wasFull = true;
            this.flashAndHide();
        }
        // During cooldown, just update the bar
        else if (this.progress < 1) {
            this.drawBar();
        }
    }

    /**
     * Flashes the bar once and then hides it.
     */
    private flashAndHide() {
        if (this.isFlashing) return;
        this.isFlashing = true;

        // Draw full bar
        this.drawBar();

        // Grow bigger (2x size, maintaining aspect ratio) and fade out animation
        // Scale the container, which will scale from its center point
        this.scene.tweens.add({
            targets: this.bar,
            alpha: 0,
            scaleX: 2,
            scaleY: 2,
            duration: 300,
            ease: 'Power2',
            onComplete: () => {
                this.bar.setVisible(false);
                this.bar.setAlpha(1);
                this.bar.setScale(1);
                this.isFlashing = false;
                this.destroy();
            }
        });
    }

    /**
     * Draws the aim cooldown bar.
     */
    private drawBar() {
        this.graphics.clear();
        // Draw bar centered in the container (so it scales from center)
        const offsetX = -this.width / 2;
        const offsetY = -this.height / 2;

        // Background (dark gray)
        this.graphics.fillStyle(0x222222);
        this.graphics.fillRect(offsetX, offsetY, this.width, this.height);
        // Aim progress (yellow/gold)
        const progressWidth = this.progress * this.width;
        this.graphics.fillStyle(0xffdd00);
        this.graphics.fillRect(offsetX, offsetY, progressWidth, this.height);
    }

    /**
     * Destroys the aim cooldown bar graphics.
     */
    destroy() {
        this.bar.destroy();
    }
}
